/**
 * Zod-free helpers for reading stringified form_response payloads.
 *
 * Kept separate from `@common/types/formSubmissions` so public client forms can
 * parse draft responses without pulling the Zod schema runtime into their bundle.
 */

/**
 * A single field response stored in `draft_form_submissions.form_response`.
 *
 * The `formResponse` column contains a JSON-stringified array of these objects.
 *
 * @example
 * // Raw JSON in database:
 * // [{"FormFieldId": "uuid-1", "FormFieldResponse": "John"}, {"FormFieldId": "uuid-2", "FormFieldResponse": null}]
 */
export interface DraftFormResponseField {
    /** Maps to `templateFormFieldId` - the UUID of the template form field */
    FormFieldId: string;
    /** The user's response value, or null if not answered */
    FormFieldResponse: string | null;
}

/**
 * A richer field response format used in `prospect_responses.form_response`.
 *
 * This format includes metadata about the field in addition to the response value.
 * Used after quote/intake submission when responses are persisted to prospect_responses.
 */
export interface ProspectFormResponseField {
    /** The field name/identifier (e.g., "firstName", "email") */
    Name: string;
    /** Human-readable field title */
    Title: string | null;
    /** The user's response value */
    Value: string;
    /** UUID of the template form field */
    TemplateFormFieldId: string;
    /** Display order for rendering */
    DisplayOrder: number;
    /** Group/section the field belongs to (e.g., "Personal Information") */
    FormGroupTitle: string | null;
    /** Whether this is a critical question for the workflow */
    IsCriticalQuestion?: boolean | null;
    /** Prefill tags from the template question, when available */
    Tags?: string[] | null;
}

/**
 * Parse the stringified `formResponse` from a draft form submission.
 *
 * @example
 * const draftResponse = await verifyClientForDraftForm(payload);
 * const fields = parseDraftFormResponse(draftResponse.formResponse);
 * const emailField = fields.find(f => f.FormFieldId === emailTemplateFieldId);
 */
export function parseDraftFormResponse(formResponse: string): DraftFormResponseField[] {
    try {
        return JSON.parse(formResponse) as DraftFormResponseField[];
    } catch {
        console.error('Failed to parse draft form response:', formResponse);
        return [];
    }
}

/**
 * Parse the stringified `formResponse` from prospect_responses.
 *
 * @example
 * const prospectResponse = await getProspectResponse(prospectId);
 * const fields = parseProspectFormResponse(prospectResponse.formResponse);
 * const firstName = fields.find(f => f.Name === 'firstName')?.Value;
 */
export function parseProspectFormResponse(formResponse: string): ProspectFormResponseField[] {
    try {
        return JSON.parse(formResponse) as ProspectFormResponseField[];
    } catch {
        console.error('Failed to parse prospect form response:', formResponse);
        return [];
    }
}

/**
 * Get a field value by templateFormFieldId from parsed draft form response.
 */
export function getDraftFieldValue(
    fields: DraftFormResponseField[],
    templateFormFieldId: string,
): string | null {
    return fields.find((f) => f.FormFieldId === templateFormFieldId)?.FormFieldResponse ?? null;
}

/**
 * Get a field value by name from parsed prospect form response.
 */
export function getProspectFieldValue(
    fields: ProspectFormResponseField[],
    fieldName: string,
): string | null {
    return fields.find((f) => f.Name === fieldName)?.Value ?? null;
}
